import { COMPANY } from "@/config/company";

const E = COMPANY.supportEmail;
const SITE = COMPANY.site;
const UPDATED = "2026-08-26";

// Ogni documento: { it: {title, sections:[{h, p:[], ul:[]}]}, en: {...} }
// Contenuti riferiti alla situazione ATTUALE (venditore persona fisica senza partita IVA).

export const legalDocs = {
  privacy: {
    it: {
      title: "Privacy Policy",
      intro: "La presente informativa descrive come vengono trattati i dati personali degli utenti di LearnSphereHub, ai sensi del Regolamento (UE) 2016/679 (GDPR).",
      sections: [
        { h: "Titolare del trattamento", p: [
          `Il titolare del trattamento è ${COMPANY.seller} (${COMPANY.status.it}), che opera con il nome commerciale ${COMPANY.brand}.`,
          `Per qualsiasi richiesta relativa ai dati personali puoi scrivere a ${E}. Sito: ${SITE}.`,
        ] },
        { h: "Dati che raccogliamo", p: ["Trattiamo le seguenti categorie di dati:"], ul: [
          "Dati di registrazione e account: nome, email e password (in forma cifrata) oppure identificativi forniti da Google se scegli l'accesso con Google.",
          "Dati relativi agli ordini e ai pagamenti: prodotti/servizi acquistati, importi e stato del pagamento (i dati della carta sono gestiti direttamente dal fornitore di pagamento).",
          "Dati inseriti per i Servizi Smart (es. CV Smart Premium): informazioni che scegli di fornire per generare il documento (nome, contatti, esperienze, istruzione, competenze, eventuale foto).",
          "Messaggi inviati tramite il modulo contatti: nome, email e testo del messaggio.",
          "Dati tecnici essenziali al funzionamento (es. archiviazione locale nel browser per lingua, carrello e sessione di accesso).",
        ] },
        { h: "Finalità e basi giuridiche", p: ["Trattiamo i dati per:"], ul: [
          "Fornire i prodotti e i servizi richiesti e gestire l'account (esecuzione del contratto, art. 6.1.b GDPR).",
          "Gestire pagamenti, fatturazione e adempimenti amministrativi (obbligo legale/esecuzione del contratto, art. 6.1.b e 6.1.c).",
          "Rispondere alle richieste inviate tramite il modulo contatti (consenso/legittimo interesse, art. 6.1.a/6.1.f).",
          "Garantire sicurezza, prevenzione degli abusi e corretto funzionamento del sito (legittimo interesse, art. 6.1.f).",
        ] },
        { h: "Fornitori e destinatari dei dati", p: [
          "Per erogare il servizio ci avvaliamo di fornitori terzi, ciascuno nel proprio ruolo:",
        ], ul: [
          "Stripe — elaborazione dei pagamenti. Stripe tratta i dati di pagamento in qualità di titolare autonomo per finalità di esecuzione del pagamento, sicurezza e adempimenti normativi.",
          "Google — accesso opzionale con account Google. Google agisce come titolare autonomo per i dati di autenticazione del proprio servizio.",
          "Piattaforma Emergent — fornitore dell'infrastruttura di hosting, dell'archiviazione dei file generati (es. PDF e foto dei CV) e del gateway per la generazione dei contenuti del CV; opera come responsabile del trattamento per nostro conto.",
          "OpenAI (tramite la piattaforma Emergent) — elaborazione del testo per la generazione/ottimizzazione del CV; opera come sub-responsabile del trattamento.",
          "Database e servizi di archiviazione utilizzati per conservare account, ordini, CV e messaggi, come parte dell'infrastruttura tecnica.",
          "IONOS — fornitore del servizio di posta elettronica (casella assistenza@learnspherehub.it) utilizzato per ricevere e gestire le richieste inviate tramite il modulo contatti.",
        ] },
        { h: "Trasferimenti extra-UE", p: [
          "Alcuni fornitori possono trattare dati anche al di fuori dell'Unione Europea. In tali casi il trasferimento avviene sulla base di garanzie adeguate previste dal GDPR (es. clausole contrattuali standard).",
        ] },
        { h: "Conservazione dei dati", p: [
          "Conserviamo i dati dell'account finché l'account resta attivo. I dati relativi a ordini e pagamenti sono conservati per il tempo necessario agli adempimenti amministrativi e di legge. I CV generati restano disponibili finché non li elimini. I messaggi del modulo contatti sono conservati per il tempo necessario a gestire la richiesta.",
        ] },
        { h: "I tuoi diritti", p: [
          "Puoi esercitare in qualsiasi momento i diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione, oltre a revocare i consensi prestati.",
          `Per esercitare i tuoi diritti scrivi a ${E}. Hai inoltre il diritto di proporre reclamo all'autorità di controllo competente (in Italia, il Garante per la protezione dei dati personali).`,
        ] },
        { h: "Sicurezza", p: [
          "Adottiamo misure tecniche e organizzative adeguate per proteggere i dati. Le password sono conservate in forma cifrata e i dati di pagamento non sono gestiti direttamente da noi.",
        ] },
        { h: "Modifiche", p: [
          "La presente informativa può essere aggiornata. Le modifiche saranno pubblicate su questa pagina con la relativa data di aggiornamento.",
        ] },
      ],
    },
    en: {
      title: "Privacy Policy",
      intro: "This notice explains how the personal data of LearnSphereHub users is processed, in accordance with Regulation (EU) 2016/679 (GDPR).",
      sections: [
        { h: "Data controller", p: [
          `The data controller is ${COMPANY.seller} (${COMPANY.status.en}), operating under the trade name ${COMPANY.brand}.`,
          `For any request regarding personal data you can write to ${E}. Website: ${SITE}.`,
        ] },
        { h: "Data we collect", p: ["We process the following categories of data:"], ul: [
          "Registration and account data: name, email and password (encrypted) or identifiers provided by Google if you choose Google sign-in.",
          "Order and payment data: purchased products/services, amounts and payment status (card data is handled directly by the payment provider).",
          "Data you enter for Smart Services (e.g. CV Smart Premium): information you choose to provide to generate the document (name, contacts, experience, education, skills, optional photo).",
          "Messages sent via the contact form: name, email and message text.",
          "Technical data essential to operation (e.g. local browser storage for language, cart and login session).",
        ] },
        { h: "Purposes and legal bases", p: ["We process data to:"], ul: [
          "Provide the requested products and services and manage the account (performance of the contract, art. 6.1.b GDPR).",
          "Handle payments, invoicing and administrative obligations (legal obligation/performance of contract, art. 6.1.b and 6.1.c).",
          "Respond to requests sent via the contact form (consent/legitimate interest, art. 6.1.a/6.1.f).",
          "Ensure security, abuse prevention and correct site operation (legitimate interest, art. 6.1.f).",
        ] },
        { h: "Providers and recipients", p: ["To deliver the service we rely on third-party providers, each in its own role:"], ul: [
          "Stripe — payment processing. Stripe processes payment data as an independent controller for payment execution, security and regulatory purposes.",
          "Google — optional sign-in with a Google account. Google acts as an independent controller for its own authentication data.",
          "Emergent platform — provider of the hosting infrastructure, storage of generated files (e.g. CV PDFs and photos) and the gateway used to generate CV content; acts as a processor on our behalf.",
          "OpenAI (via the Emergent platform) — text processing for CV generation/optimisation; acts as a sub-processor.",
          "Database and storage services used to keep accounts, orders, CVs and messages, as part of the technical infrastructure.",
          "IONOS — email service provider (assistenza@learnspherehub.it mailbox) used to receive and handle requests sent through the contact form.",
        ] },
        { h: "Transfers outside the EU", p: [
          "Some providers may process data outside the European Union. In such cases transfers rely on appropriate safeguards under the GDPR (e.g. standard contractual clauses).",
        ] },
        { h: "Data retention", p: [
          "We keep account data while the account is active. Order and payment data are kept as long as necessary for administrative and legal obligations. Generated CVs remain available until you delete them. Contact messages are kept as long as needed to handle the request.",
        ] },
        { h: "Your rights", p: [
          "You may exercise at any time the rights of access, rectification, erasure, restriction, portability and objection, and withdraw any consent given.",
          `To exercise your rights write to ${E}. You also have the right to lodge a complaint with the competent supervisory authority.`,
        ] },
        { h: "Security", p: [
          "We adopt appropriate technical and organisational measures to protect data. Passwords are stored encrypted and payment data is not handled directly by us.",
        ] },
        { h: "Changes", p: [
          "This notice may be updated. Changes will be published on this page with the relevant update date.",
        ] },
      ],
    },
  },

  cookie: {
    it: {
      title: "Cookie Policy",
      intro: "Questa pagina spiega quali strumenti di archiviazione e cookie utilizza LearnSphereHub.",
      sections: [
        { h: "Archiviazione tecnica", p: [
          "Il sito utilizza esclusivamente archiviazione tecnica locale nel tuo browser (localStorage) per ricordare la lingua selezionata, il contenuto del carrello e mantenere la sessione di accesso. Questi strumenti sono necessari al funzionamento del sito.",
        ] },
        { h: "Analitica e strumenti della piattaforma", p: [
          "Il sito è attualmente erogato tramite la piattaforma Emergent, che carica automaticamente alcuni strumenti di analisi e diagnostica non gestiti dal nostro codice applicativo.",
          "Durante la navigazione, prima di qualsiasi scelta dell'utente, sono stati rilevati strumenti di tipo PostHog (analitica di prodotto, registrazione delle sessioni e acquisizione automatica delle interazioni/click) e di monitoraggio delle prestazioni (web vitals), caricati dal dominio ap.emergent.sh, oltre a Cloudflare Web Analytics (static.cloudflareinsights.com).",
          "Questi strumenti non sono controllabili dal nostro codice: stiamo verificando la possibilità di disattivarli o limitarli a livello di piattaforma. Fino al completamento della verifica, questa pagina riporta quanto effettivamente rilevato.",
        ] },
        { h: "Font", p: [
          "Vengono caricati i Google Fonts dai domini fonts.googleapis.com e fonts.gstatic.com per la resa tipografica.",
        ] },
        { h: "Strumenti di terze parti", p: [
          "Durante il pagamento (Stripe) e l'accesso con Google possono essere impostati cookie tecnici da parte di questi fornitori, necessari a completare l'operazione. Tali cookie sono gestiti dai rispettivi fornitori secondo le loro policy.",
        ] },
        { h: "Gestione delle preferenze", p: [
          "Puoi cancellare o bloccare l'archiviazione locale e i cookie dalle impostazioni del tuo browser. La disattivazione di questi strumenti può compromettere alcune funzionalità del sito.",
        ] },
      ],
    },
    en: {
      title: "Cookie Policy",
      intro: "This page explains which storage tools and cookies LearnSphereHub uses.",
      sections: [
        { h: "Technical storage", p: [
          "The site uses only local technical storage in your browser (localStorage) to remember the selected language, the cart content and to keep the login session. These tools are necessary for the site to work.",
        ] },
        { h: "Platform analytics and tools", p: [
          "The site is currently delivered through the Emergent platform, which automatically loads certain analytics and diagnostic tools that are not managed by our application code.",
          "During browsing, before any user choice, we detected PostHog-type tools (product analytics, session recording and automatic capture of interactions/clicks) and performance monitoring (web vitals), loaded from the ap.emergent.sh domain, as well as Cloudflare Web Analytics (static.cloudflareinsights.com).",
          "These tools are not controllable from our code: we are verifying whether they can be disabled or limited at platform level. Until this verification is complete, this page reports what was actually detected.",
        ] },
        { h: "Fonts", p: [
          "Google Fonts are loaded from fonts.googleapis.com and fonts.gstatic.com for typography.",
        ] },
        { h: "Third-party tools", p: [
          "During payment (Stripe) and Google sign-in, technical cookies may be set by these providers as needed to complete the operation. Such cookies are managed by the respective providers under their own policies.",
        ] },
        { h: "Managing preferences", p: [
          "You can delete or block local storage and cookies from your browser settings. Disabling these tools may affect some site features.",
        ] },
      ],
    },
  },

  terms: {
    it: {
      title: "Termini e condizioni",
      intro: "I presenti termini regolano l'acquisto di prodotti digitali e servizi su LearnSphereHub.",
      sections: [
        { h: "Venditore", p: [
          `Venditore: ${COMPANY.seller} (${COMPANY.status.it}), nome commerciale ${COMPANY.brand}.`,
          `Email di assistenza: ${E} — Sito: ${SITE}.`,
        ] },
        { h: "Oggetto", p: [
          "LearnSphereHub offre prodotti digitali scaricabili e servizi smart personalizzati (ad esempio CV Smart Premium). Le caratteristiche di ciascun prodotto o servizio sono descritte nelle relative pagine.",
        ] },
        { h: "Prezzi e pagamenti", p: [
          "I prezzi sono indicati in Euro nelle pagine di prodotto e nel checkout. Eventuali importi e imposte applicabili sono mostrati prima della conferma dell'ordine.",
          "I pagamenti sono elaborati in modo sicuro tramite il fornitore di pagamento Stripe. Non conserviamo i dati della tua carta.",
        ] },
        { h: "Conclusione del contratto", p: [
          "Il contratto si intende concluso quando ricevi la conferma dell'ordine/pagamento. Per acquistare è necessario essere registrati e accettare i presenti termini.",
        ] },
        { h: "Consegna ed erogazione", p: [
          "I prodotti digitali sono resi disponibili per il download dopo il completamento del pagamento. I servizi smart vengono erogati a seguito della richiesta e del completamento del pagamento, con consegna del risultato in formato digitale (PDF).",
        ] },
        { h: "Diritto di recesso", p: [
          "Le condizioni di recesso per i consumatori e le relative eccezioni per contenuti e servizi digitali sono descritte nella pagina \"Recesso e rimborsi\".",
        ] },
        { h: "Assistenza", p: [
          `Per assistenza, informazioni o reclami puoi scrivere a ${E}.`,
        ] },
        { h: "Legge applicabile e foro", p: [
          "I presenti termini sono regolati dalla legge italiana, fatti salvi gli eventuali diritti inderogabili riconosciuti al consumatore dalla legge del proprio Paese di residenza.",
          "Per i consumatori è competente, in via esclusiva, il foro del luogo di residenza o domicilio del consumatore.",
        ] },
        { h: "Limitazione di responsabilità", p: [
          "I contenuti e i modelli offerti hanno finalità di supporto professionale. Il venditore non garantisce risultati specifici derivanti dall'uso dei documenti generati.",
        ] },
        { h: "Modifiche", p: [
          "I presenti termini possono essere aggiornati; la versione applicabile è quella pubblicata al momento dell'ordine.",
        ] },
      ],
    },
    en: {
      title: "Terms & Conditions",
      intro: "These terms govern the purchase of digital products and services on LearnSphereHub.",
      sections: [
        { h: "Seller", p: [
          `Seller: ${COMPANY.seller} (${COMPANY.status.en}), trade name ${COMPANY.brand}.`,
          `Support email: ${E} — Website: ${SITE}.`,
        ] },
        { h: "Scope", p: [
          "LearnSphereHub offers downloadable digital products and personalised smart services (e.g. CV Smart Premium). The features of each product or service are described on the relevant pages.",
        ] },
        { h: "Prices and payments", p: [
          "Prices are shown in Euro on product pages and at checkout. Any applicable amounts and taxes are displayed before order confirmation.",
          "Payments are processed securely through the payment provider Stripe. We do not store your card data.",
        ] },
        { h: "Conclusion of the contract", p: [
          "The contract is concluded when you receive the order/payment confirmation. To purchase you must be registered and accept these terms.",
        ] },
        { h: "Delivery", p: [
          "Digital products are made available for download after payment is completed. Smart services are delivered following the request and payment completion, with the result delivered in digital format (PDF).",
        ] },
        { h: "Right of withdrawal", p: [
          "Withdrawal conditions for consumers and the related exceptions for digital content and services are described on the \"Withdrawal & Refunds\" page.",
        ] },
        { h: "Support", p: [
          `For support, information or complaints you can write to ${E}.`,
        ] },
        { h: "Governing law and jurisdiction", p: [
          "These terms are governed by Italian law, without prejudice to any mandatory rights granted to consumers by the law of their country of residence.",
          "For consumers, the court of the consumer's place of residence or domicile has exclusive jurisdiction.",
        ] },
        { h: "Limitation of liability", p: [
          "The content and templates offered are intended as professional support. The seller does not guarantee specific results from the use of the generated documents.",
        ] },
        { h: "Changes", p: [
          "These terms may be updated; the applicable version is the one published at the time of the order.",
        ] },
      ],
    },
  },

  refund: {
    it: {
      title: "Recesso e rimborsi",
      intro: "Informazioni sul diritto di recesso dei consumatori e sui rimborsi.",
      sections: [
        { h: "Diritto di recesso", p: [
          "Se sei un consumatore, hai in generale diritto di recedere dal contratto entro 14 giorni, senza dover fornire motivazioni, salvo le eccezioni previste dalla legge per i contenuti e i servizi digitali indicate di seguito.",
        ] },
        { h: "Contenuti digitali a download immediato", p: [
          "Per i prodotti digitali forniti tramite download, al momento dell'acquisto ti viene chiesto di confermare la fornitura immediata del contenuto e di riconoscere che, una volta che la fornitura ha inizio, il diritto di recesso non può più essere esercitato.",
        ] },
        { h: "Servizi smart", p: [
          "Per i servizi smart (ad esempio CV Smart Premium), al momento dell'acquisto ti viene chiesto di richiedere l'avvio immediato del servizio e di riconoscere che il diritto di recesso si perde una volta completata l'esecuzione del servizio.",
        ] },
        { h: "Come esercitare il recesso", p: [
          `Quando applicabile, puoi esercitare il recesso inviando una comunicazione a ${E}, indicando l'ordine di riferimento.`,
        ] },
        { h: "Rimborsi", p: [
          "Se il recesso è applicabile o in caso di rimborso dovuto, l'importo viene restituito utilizzando lo stesso metodo di pagamento impiegato per l'acquisto, entro i termini di legge.",
        ] },
        { h: "Prodotti non conformi o non funzionanti", p: [
          `Se un prodotto o servizio digitale risulta difettoso o non conforme a quanto descritto, contattaci a ${E}: provvederemo alla risoluzione, alla nuova erogazione o al rimborso secondo quanto previsto dalla legge.`,
        ] },
      ],
    },
    en: {
      title: "Withdrawal & Refunds",
      intro: "Information about the consumer right of withdrawal and refunds.",
      sections: [
        { h: "Right of withdrawal", p: [
          "If you are a consumer, you generally have the right to withdraw from the contract within 14 days, without giving reasons, save for the legal exceptions for digital content and services described below.",
        ] },
        { h: "Digital content with immediate download", p: [
          "For digital products delivered via download, at the time of purchase you are asked to confirm immediate supply of the content and to acknowledge that, once supply begins, the right of withdrawal can no longer be exercised.",
        ] },
        { h: "Smart services", p: [
          "For smart services (e.g. CV Smart Premium), at the time of purchase you are asked to request immediate start of the service and to acknowledge that the right of withdrawal is lost once the service has been fully performed.",
        ] },
        { h: "How to withdraw", p: [
          `Where applicable, you can withdraw by sending a message to ${E}, stating the relevant order.`,
        ] },
        { h: "Refunds", p: [
          "Where withdrawal applies or a refund is due, the amount is returned using the same payment method used for the purchase, within the legal timeframe.",
        ] },
        { h: "Faulty or non-conforming products", p: [
          `If a digital product or service is faulty or does not match its description, contact us at ${E}: we will remedy, re-deliver or refund as required by law.`,
        ] },
      ],
    },
  },
};

export const UPDATED_LABEL = UPDATED;
